import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Dashboard, DashboardCreate } from '@src/main/modules/common/dashboard/domain/Dashboard';
import { useAreaContext } from '@src/main/ui/providers/area/AreaContextProvider';
import { useDashboardContext } from '@src/main/ui/providers/dashboard/DashboardContextProvider';
import { useSnackbarContext } from '@src/main/ui/providers/snackbar/SnackbarContextProvider';
import { v4 as uuidv4 } from 'uuid';

import AdminAddDashboardInfoModal from '@src/main/ui/components/AdminAddDashboards/common/AdminAddDashboards/AdminAddDashboardModal/AdminAddDashboardInfoModal/AdminAddDashboardInfoModal';
import Card from '@src/main/ui/components/common/Card/Card';
import Icon from '@src/main/ui/components/common/Icon/Icon';
import Modal from '@src/main/ui/components/common/Modal/Modal';

interface Props {
  dashboard: DashboardCreate | null;
  onClose: () => void;
}

interface ArtifactType {
  artifactId: string;
  workspaceId: string | undefined;
  type: string | null;
}

const AdminAddDashboardModal: FC<Props> = ({ dashboard, onClose }) => {
  const { areaId, subareaId, subsubareaId } = useParams();
  const [modalInfoOpen, setModalInfoOpen] = useState(false);
  const [dashboardFields, setDashboardFields] = useState<DashboardCreate>(
    dashboard
      ? { ...dashboard, areaId, subareaId, subsubareaId }
      : {
          id: uuidv4(),
          name: '',
          artifactType: 'PowerBIReport',
          keyTags: [],
          tags: [],
          description: '',
          isCertified: false,
          showAll: true,
          artifactUrl: '',
          areaId,
          subareaId,
          subsubareaId,
          repositoryType: null,
        },
  );
  const [loadingSave, setLoadingSave] = useState(false);

  const { dashboards, addDashboard } = useDashboardContext();
  const { areas } = useAreaContext();
  const { openSnackbar } = useSnackbarContext();

  const handleChange = (value: string | boolean | string[], field: string) => {
    setDashboardFields({ ...dashboardFields, [field]: value });
  };

  const openInfo = () => {
    setModalInfoOpen(true);
  };

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const artifact = getArtifactFromUrl(
      dashboardFields.artifactType,
      dashboardFields.artifactUrl,
    ) as ArtifactType;
    if (!artifact) {
      openSnackbar({
        message: 'Invalid URL',
        severity: 'error',
      });
    } else {
      dashboardFields.artifactId = artifact.artifactId;
      dashboardFields.workspaceId = artifact.workspaceId;
      dashboardFields.repositoryType = artifact.type;
      setLoadingSave(true);
      addDashboard(dashboardFields)
        .then(() => onClose())
        .catch(() => {
          openSnackbar({
            message: 'Insufficient permissions for adding the dashboard',
            severity: 'error',
          });
        })
        .finally(() => setLoadingSave(false));
    }
  };

  return (
    <>
      <Modal open handleClose={onClose}>
        <Card width="90vw" maxWidth="650px" maxHeight="75vh">
          <form onSubmit={submit}>
            <Box
              sx={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              <Typography variant="h5">{!dashboard ? 'Add' : 'Edit'} dashboard</Typography>
              <TextField
                sx={{
                  width: '100%',
                }}
                value={dashboardFields.name}
                variant="standard"
                label="Name"
                required
                onChange={(e) => handleChange(e.target.value, 'name')}
              />
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                }}
              >
                <InputLabel id="artifact-type-label">Type</InputLabel>
                <Select
                  native
                  required
                  labelId="artifact-type-label"
                  label="Type"
                  value={dashboardFields.artifactType}
                  onChange={(e) => handleChange(e.target.value, 'artifactType')}
                >
                  <option value="LookerStudio">Looker Studio</option>
                  <option value="PowerApp">PowerApp</option>
                  <option value="PowerBIReport">PowerBI report</option>
                  <option value="PowerBIRepository">PowerBI repository</option>
                </Select>
              </FormControl>
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                }}
              >
                <InputLabel id="area-label">Great area</InputLabel>
                <Select
                  native
                  required
                  labelId="area-label"
                  label="Great area"
                  value={dashboardFields.areaId}
                  onChange={(e) => handleChange(e.target.value, 'areaId')}
                >
                  {areas?.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                }}
              >
                <InputLabel id="subarea-label">Area</InputLabel>
                <Select
                  required
                  native
                  labelId="subarea-label"
                  label="Area"
                  value={dashboardFields.subareaId}
                  onChange={(e) => handleChange(e.target.value, 'subareaId')}
                >
                  {areas
                    ?.find((area) => area.id === dashboardFields.areaId)
                    ?.subareas.map((subarea) => (
                      <option key={subarea.id} value={subarea.id}>
                        {subarea.name}
                      </option>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                variant="standard"
                sx={{
                  width: '100%',
                }}
              >
                <InputLabel id="subsubarea-label">Subarea</InputLabel>
                <Select
                  required
                  native
                  labelId="subsubarea-label"
                  label="Subarea"
                  value={dashboardFields.subsubareaId}
                  onChange={(e) => handleChange(e.target.value, 'subsubareaId')}
                >
                  {areas
                    ?.find((area) => area.id === dashboardFields.areaId)
                    ?.subareas.find((subarea) => subarea.id === dashboardFields.subareaId)
                    ?.subsubareas.map((subsubarea) => (
                      <option key={subsubarea.id} value={subsubarea.id}>
                        {subsubarea.name}
                      </option>
                    ))}
                </Select>
              </FormControl>
              <TextField
                sx={{
                  width: '100%',
                }}
                value={dashboardFields.artifactUrl}
                variant="standard"
                label="URL"
                required
                onChange={(e) => handleChange(e.target.value, 'artifactUrl')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment sx={{ cursor: 'pointer' }} position="start" onClick={openInfo}>
                      <Icon iconId="info" />
                    </InputAdornment>
                  ),
                }}
              />
              {modalInfoOpen && (
                <AdminAddDashboardInfoModal
                  onClose={() => setModalInfoOpen(false)}
                  type={dashboardFields.artifactType}
                />
              )}
              <TextField
                sx={{
                  width: '100%',
                }}
                required
                value={dashboardFields.description}
                variant="standard"
                label="Description"
                multiline
                onChange={(e) => handleChange(e.target.value, 'description')}
              />
              <Autocomplete
                multiple
                options={getKeyTags(dashboards)}
                freeSolo
                onChange={(_, newValue) => handleChange(newValue, 'keyTags')}
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                value={dashboardFields.keyTags}
                renderInput={(params) => (
                  <TextField {...params} variant="standard" label="Key tags" />
                )}
              />
              <Autocomplete
                multiple
                options={getTags(dashboards)}
                freeSolo
                onChange={(_, newValue) => handleChange(newValue, 'tags')}
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                value={dashboardFields.tags}
                renderInput={(params) => <TextField {...params} variant="standard" label="Tags" />}
              />
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={dashboardFields.isCertified}
                    onChange={(e) => handleChange(e.target.checked, 'isCertified')}
                  />
                }
                label="Is certified?"
              />
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={dashboardFields.showAll}
                    onChange={(e) => handleChange(e.target.checked, 'showAll')}
                  />
                }
                label="Show to all company?"
              />
              <Button
                sx={{
                  width: 'fit-content',
                  alignSelf: 'flex-end',
                }}
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loadingSave}
              >
                {!dashboard ? 'Add' : 'Save'}
              </Button>
            </Box>
          </form>
        </Card>
      </Modal>
    </>
  );
};

const getKeyTags = (dashboards: Dashboard[] | undefined) =>
  getNotRepeatedProperty(dashboards, 'keyTags');

const getTags = (dashboards: Dashboard[] | undefined) => getNotRepeatedProperty(dashboards, 'tags');

const getNotRepeatedProperty = (
  dashboards: Dashboard[] | undefined,
  property: 'keyTags' | 'tags',
) => {
  if (!dashboards) return [];
  const keyTags = dashboards
    .reduce((acc: string[], dashboard: Dashboard) => [...acc, ...dashboard[property]], [])
    .sort((a, b) => a.localeCompare(b));

  return [...new Set(keyTags)];
};

const getArtifactFromUrl = (type: string, url: string) => {
  if (type === 'LookerStudio') {
    return getArtifactFromUrlLookerStudio(url);
  }
  if (type === 'PowerBIReport') {
    return getArtifactFromUrlPowerBIReport(url);
  }
  if (type === 'PowerBIRepository') {
    return getArtifactFromUrlPowerBIRepository(url);
  }
  if (type === 'PowerApp') {
    return getArtifactFromUrlPowerApp(url);
  }
  return null;
};

const getArtifactFromUrlLookerStudio = (url: string) => {
  const splitted = url.split('/');
  const indexReporting = splitted.findIndex((part) => part === 'reporting');
  if (indexReporting === -1 || indexReporting === splitted.length - 1) return null;
  return {
    workspaceId: undefined,
    artifactId: splitted[indexReporting + 1],
  };
};

const getArtifactFromUrlPowerBIReport = (url: string) => {
  const urlParts = url.split(/\/|\?/); // split by / or ?
  const indexGroups = urlParts.findIndex((part) => part === 'groups');
  const indexReports = urlParts.findIndex((part) => part === 'reports');
  if (indexGroups === -1 || indexReports === -1) return null;
  const workspaceId = urlParts[indexGroups + 1];
  const artifactId = urlParts[indexReports + 1];
  return {
    workspaceId,
    artifactId,
  };
};

const getArtifactFromUrlPowerBIRepository = (url: string) => {
  const regexAppId = /appId=([a-f\d-]+)/;
  const regexDashboardObjectId = /dashboardObjectId=([a-f\d-]+)/;
  const regexReportObjectId = /reportObjectId=([a-f\d-]+)/;

  const matchAppId = url.match(regexAppId);
  const matchDashboardObjectId = url.match(regexDashboardObjectId);
  const matchReportObjectId = url.match(regexReportObjectId);

  if (matchAppId && (matchDashboardObjectId || matchReportObjectId)) {
    const appId = matchAppId[1];
    const dashboardObjectId = matchDashboardObjectId ? matchDashboardObjectId[1] : undefined;
    const reportObjectId = matchReportObjectId ? matchReportObjectId[1] : undefined;

    return {
      workspaceId: appId,
      artifactId: dashboardObjectId ?? reportObjectId,
      type: dashboardObjectId ? 'dashboards' : 'reports',
    };
  }
  return null;
};

const getArtifactFromUrlPowerApp = (url: string) => {
  const urlSegments = url.split('/');
  const indexApp = urlSegments.findIndex((part) => part === 'a');
  if (indexApp === -1) return null;
  const artifactId = urlSegments[indexApp + 1];
  return {
    workspaceId: undefined,
    artifactId: artifactId.split('?')[0],
  };
};

export default AdminAddDashboardModal;
